/* eslint-disable @stylistic/max-len */
import type {RefObject} from 'react';

/**
 * The `HelpOutlineIconProps` interface defines the properties for the HelpOutlineIcon component.
 */
interface HelpOutlineIconProps {
    /** CSS class to override default styling. */
    className?: string;
    /** Primary color of the icon. */
    color1?: string;
    /** Height of the icon. */
    height?: number | string;
    ref?: RefObject<SVGSVGElement>;
    /** Test ID for Cypress. */
    testId?: string;
    /** Width of the icon. */
    width?: number | string;
}

/**
 * Material Symbol "help" — outlined, weight 300.
 * Question mark icon for FAQ navigation tab.
 *
 * @param props           The component props.
 * @param props.className CSS class override.
 * @param props.color1    Primary icon color.
 * @param props.height    Icon height.
 * @param props.ref       SVG element ref.
 * @param props.testId    Test identifier.
 * @param props.width     Icon width.
 * @returns A React SVG element.
 */
export const HelpOutlineIcon = ({
    className = '',
    color1 = 'currentColor',
    height,
    ref,
    testId = 'HelpOutlineIcon',
    width
}: HelpOutlineIconProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        fill={color1}
        height={height}
        viewBox="0 -960 960 960"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M479.96-259.23q14.73 0 24.77-10.01 10.04-10 10.04-24.72 0-14.73-10-24.77-10.01-10.04-24.73-10.04-14.73 0-24.77 10-10.04 10.01-10.04 24.73 0 14.73 10 24.77 10.01 10.04 24.73 10.04Zm-29.88-155.69h55.38q1.54-28.46 11.16-46.85 9.61-18.38 38.84-46.84 27.31-27.31 42.08-50.47 14.77-23.15 14.77-55.3 0-51.97-35.52-81.2-35.52-29.22-82.79-29.22-43.31 0-76.62 22.26-33.3 22.27-49.61 58.62l50.15 20.07q11.54-25.84 31.66-41.57 20.11-15.73 46.11-15.73 30.93 0 49.35 17.23 18.42 17.23 18.42 43.46 0 21.54-12.38 40.04-12.39 18.5-36.93 41.04-31.38 28.61-43.23 51.96-11.84 23.35-11.84 72.5ZM480.07-100q-78.84 0-148.21-29.92t-120.68-81.21q-51.31-51.29-81.25-120.63Q100-401.1 100-479.93q0-78.84 29.92-148.21t81.21-120.68q51.29-51.31 120.63-81.25Q401.1-860 479.93-860q78.84 0 148.21 29.92t120.68 81.21q51.31 51.29 81.25 120.63Q860-558.9 860-480.07q0 78.84-29.92 148.21t-81.21 120.68q-51.29 51.31-120.63 81.25Q558.9-100 480.07-100Zm-.07-60q133.38 0 226.69-93.31T800-480q0-133.38-93.31-226.69T480-800q-133.38 0-226.69 93.31T160-480q0 133.38 93.31 226.69T480-160Zm0-320Z" />
    </svg>
);

HelpOutlineIcon.displayName = 'HelpOutlineIcon';